<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMessageFormatter } from 'shared/composables/useMessageFormatter';

import CardLayout from 'dashboard/components-next/CardLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import WhatsAppCampaignStatusBadge from './WhatsAppCampaignStatusBadge.vue';

// A dedicated card for WhatsApp V1 campaigns - deliberately NOT the shared
// CampaignCard (still used unmodified by Live Chat/SMS/Twilio), since V1's
// full status set (draft/scheduled/processing/paused/completed/
// completed_with_errors/cancelled/failed) and recipient-progress display
// have nothing in common with those simpler campaign types.
const props = defineProps({
  campaign: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['open', 'delete']);

const { t } = useI18n();
const { formatMessage } = useMessageFormatter();

const isTerminal = computed(() =>
  ['completed', 'completed_with_errors', 'cancelled', 'failed'].includes(
    props.campaign.campaign_status
  )
);

const scheduledAtLabel = computed(() => {
  if (!props.campaign.scheduled_at) return '';
  return new Date(props.campaign.scheduled_at * 1000).toLocaleString();
});

const audienceSnapshotCount = computed(
  () => props.campaign.audience_snapshot_count
);
</script>

<template>
  <CardLayout layout="row">
    <div class="flex flex-col items-start justify-between flex-1 min-w-0 gap-2">
      <div class="flex justify-between gap-3 w-fit">
        <span
          class="text-base font-medium capitalize text-n-slate-12 line-clamp-1"
        >
          {{ campaign.title }}
        </span>
        <WhatsAppCampaignStatusBadge :status="campaign.campaign_status" />
      </div>
      <div
        v-dompurify-html="formatMessage(campaign.message, false, false, false)"
        class="text-sm text-n-slate-11 line-clamp-1 [&>p]:mb-0 h-6"
      />
      <div class="flex items-center w-full gap-3 text-xs text-n-slate-11">
        <span
          v-if="
            audienceSnapshotCount !== null &&
            audienceSnapshotCount !== undefined
          "
        >
          {{
            t('CAMPAIGN.WHATSAPP.V1_CARD.RECIPIENT_COUNT', {
              count: audienceSnapshotCount,
            })
          }}
        </span>
        <span v-if="scheduledAtLabel">
          {{ t('CAMPAIGN.WHATSAPP.V1_CARD.SCHEDULED_FOR') }}
          {{ scheduledAtLabel }}
        </span>
      </div>
    </div>
    <div class="flex items-center justify-end gap-2">
      <Button
        variant="faded"
        color="slate"
        size="sm"
        :label="t('CAMPAIGN.WHATSAPP.V1_CARD.VIEW')"
        @click="emit('open', campaign)"
      />
      <Button
        v-if="!isTerminal"
        variant="faded"
        color="ruby"
        size="sm"
        icon="i-lucide-trash"
        @click="emit('delete', campaign)"
      />
    </div>
  </CardLayout>
</template>
